import { BookIcon, PlayIcon } from "@xola/icons";
import { Alert } from "@xola/ui-kit";
import clsx from "clsx";
import { isEmpty, trimEnd, trimStart } from "lodash";
import React, { useState } from "react";
import { tutorialsApi } from "../../api/tutorialsApi";
import { useUser } from "../../hooks/useAccount";
import { Card, Content, ContentType } from "../../types/Card";

interface PlayerProps {
    _hashedId: string;
    replaceWith: (videoId: string) => void;
    email: (email?: string) => void;
    remove: () => void;
}

export const CardDisplay = ({ card }: { card: Card }) => {
    const imageEmbed = card.body.find((body) => body.type === ContentType.IMAGE);
    const videoEmbed = card.body.find((body) => body.type === ContentType.EMBED);
    const onlyHelp = card.body.length === 0 && card.helpUrl && card.helpUrl?.length > 0;
    const hasBoth = !!imageEmbed && !!videoEmbed;

    const user = useUser();

    const [player, setPlayer] = useState<PlayerProps | null>(null);
    const videoId = videoEmbed ? getWistiaId(videoEmbed.value) : null;

    if (videoId && player && player._hashedId !== videoId) {
        console.debug(`#### Replacing ${player._hashedId} with ${videoId}`);
        player.replaceWith(videoId);
    }

    if (player && !videoEmbed) {
        player.remove();
        setPlayer(null);
    }

    const handleVideoOnReady = (video: PlayerProps) => {
        if (!player || (player && player?._hashedId !== video._hashedId)) {
            setPlayer(video);
        }
    };

    if (player) {
        player.email(user.email);
    }

    return (
        <div>
            {card.body.map((content) => {
                const { value } = content;
                if (content.type === ContentType.IMAGE) {
                    return <ImageContent key={value} content={content} />;
                }

                if (content.type === ContentType.EMBED && !hasBoth) {
                    return <InlineVideo key={value} content={content} player={player} onReady={handleVideoOnReady} />;
                }

                if (videoEmbed) {
                    // If we reach here means we have a image & video. So we skip this, and show it below
                    return null;
                }

                return (
                    <div key={value} className="h-60">
                        {value}
                    </div>
                );
            })}

            <div className="mx-auto w-full p-4 text-center">
                <div className={clsx(onlyHelp ? "h-60" : "space-x-4")}>
                    {card.helpUrl && (
                        <span>
                            <BookIcon size="medium" className="mr-1" />
                            <a
                                target="_blank"
                                rel="noreferrer"
                                href={card.helpUrl}
                                className="font-bold text-black underline"
                            >
                                Learn More
                            </a>
                        </span>
                    )}
                    {hasBoth && <VideoLink content={videoEmbed} />}
                </div>
            </div>
        </div>
    );
};

const baseTutorialsUrl = trimEnd(tutorialsApi.defaults.baseURL, "/");
const ImageContent = ({ content }: { content: Content }) => {
    let imageUrl = content.value;
    if (!imageUrl?.includes("http") && !isEmpty(imageUrl)) {
        imageUrl = `${baseTutorialsUrl.replace("/api", "")}/${trimStart(imageUrl, "/")}`;
    }

    return <img alt={content.value} src={imageUrl} />;
};

const VideoLink = ({ content }: { content: Content }) => {
    const wistiaVideoId = getWistiaId(content.value);
    if (!wistiaVideoId) {
        return null;
    }

    const wistiaClasses = `wistia_embed wistia_async_${wistiaVideoId} popover=true popoverContent=link`;
    injectWistiaScriptTag(content.value);

    return (
        <span>
            <PlayIcon size="medium" className="mr-1" />
            <span className={clsx(`${wistiaClasses} inline-block cursor-pointer font-bold text-black underline`)}>
                Watch Video
            </span>
        </span>
    );
};

interface VideoContentProps {
    content: Content;
    onReady?: any;
    player: PlayerProps | null;
}

export const InlineVideo = ({ content, onReady, player }: VideoContentProps) => {
    const [isLoaded, setIsLoaded] = useState(false);
    window._wq = window._wq || []; // To get a video player handle https://wistia.com/support/developers/player-api#use-window-_wq-to-get-a-video-handle

    // This will inject a wistia.com script tag for this video and return the video ID
    const videoId = getWistiaId(content.value);
    if (!videoId) {
        console.log("%cError: No wistia ID found", "color:white;background-color:red;", content.value);
        return <Alert color="danger">Error: No video found</Alert>;
    }

    const handleOnLoad = () => {
        setIsLoaded(true);
        window._wq.push({
            id: videoId,
            onReady: (video: PlayerProps) => onReady(video),
        });
    };

    if (!player) {
        // Only inject the wistia script if there is no existing player
        injectWistiaScriptTag(content.value);
    }

    if (!content.value.includes("wistia.com")) {
        console.log("%cNon-wistia videos not supported", "color:white;background-color:red;", content.value);
        return <Alert>This video type is not supported</Alert>;
    }

    return (
        <div className={clsx(`wistia_embed wistia_async_${videoId} seo=false videoFoam=true relative h-56 w-full`)}>
            <div
                className={clsx(
                    "wistia-swatch h-60 transition-opacity duration-300",
                    isLoaded ? "opacity-100" : "opacity-0",
                )}
            >
                <img
                    src={`https://fast.wistia.com/embed/medias/${videoId}/swatch`}
                    alt={videoId}
                    className="h-60 w-full bg-blue-lighter object-cover blur"
                    onLoad={handleOnLoad}
                />
            </div>
        </div>
    );
};

const getWistiaId = (url: string) => {
    const matches = /medias\/([a-z\d]+)/i.exec(url);
    return matches ? matches[1] : null;
};

const injectWistiaScriptTag = (url: string) => {
    const videoId = getWistiaId(url);
    if (!videoId) {
        return null;
    }

    const wistiaScriptEmbed = document.querySelector("#wistiaScriptEmbed");
    if (wistiaScriptEmbed?.getAttribute("src")?.includes(videoId)) {
        return videoId;
    }

    const script = document.createElement("script");
    script.src = `https://fast.wistia.com/embed/medias/${videoId}.jsonp`;
    script.id = `wistiaScriptEmbed`;
    script.async = true;

    document.body.append(script);

    return videoId;
};
